import type { Company } from "@domain/CRM/company";
import type { User } from "@domain/Auth";
import { sortCompanies } from "@domain/CRM/job";

export interface CompaniesUseCasesDeps {
	getCompaniesByUser(user: User): Promise<Company[]>;
}

/**
 * Result of resolving the initial company on page-load. The vm uses
 * `shouldWriteToUrl` to decide whether to push the company id back into
 * the URL — this is true when the URL didn't already encode a valid
 * choice AND the company list has more than one entry (so the URL
 * disambiguates which company is in view).
 */
export interface InitialCompanyResolution {
	selected: Company | null;
	companies: Company[];
	shouldWriteToUrl: boolean;
}

/**
 * Use cases that operate on companies.
 *
 * `loadForUser` is the read query (sorted list). `resolveInitialCompany`
 * encodes the URL-vs-first-vs-null resolution policy the company-context
 * vm needs at mount: URL wins when valid, else the first company, else
 * null. The vm handles the actual `setSearchParams` call — this method
 * just tells it whether to.
 */
export interface CompaniesUseCases {
	loadForUser(user: User): Promise<Company[]>;
	/**
	 * Resolves which company should be selected at page-load given the user
	 * and the current URL `company` param.
	 *
	 *  - URL param matches a company → that one (no URL write).
	 *  - URL param doesn't match → fall back to first; write URL only when
	 *    there's a real first.
	 *  - No URL param + only one company → that one, no URL write.
	 *  - No URL param + multiple companies → first one, write URL (so the
	 *    next refresh deep-links to the same company).
	 *  - No companies → null, no URL write.
	 */
	resolveInitialCompany(user: User, urlCompanyId: string | null): Promise<InitialCompanyResolution>;
}

export async function loadForUser(deps: CompaniesUseCasesDeps, user: User): Promise<Company[]> {
	const fetched = await deps.getCompaniesByUser(user);
	return sortCompanies(fetched);
}

export async function resolveInitialCompany(
	deps: CompaniesUseCasesDeps,
	user: User,
	urlCompanyId: string | null,
): Promise<InitialCompanyResolution> {
	const companies = await loadForUser(deps, user);
	if (urlCompanyId) {
		const match = companies.find((c) => c.id === urlCompanyId);
		if (match) return { selected: match, companies, shouldWriteToUrl: false };
		const first = companies[0] ?? null;
		return { selected: first, companies, shouldWriteToUrl: !!first };
	}
	if (companies.length === 0) return { selected: null, companies, shouldWriteToUrl: false };
	const first = companies[0]!;
	return { selected: first, companies, shouldWriteToUrl: companies.length > 1 };
}

export function makeCompaniesUseCases(deps: CompaniesUseCasesDeps): CompaniesUseCases {
	return {
		loadForUser: (user) => loadForUser(deps, user),
		resolveInitialCompany: (user, urlCompanyId) => resolveInitialCompany(deps, user, urlCompanyId),
	};
}
